// The public forms, described once (OEL-172).
//
// Contact and Complaint each validated themselves twice: a few `required`
// attributes in the browser and a hand-written block in the route. The two
// never had to agree, and neither could tell the other what a field is called
// or how long it may be. This registry is the single description, used by the
// page to render and check, and by the server to reject.
//
// The admission form has its own richer registry (admissionFormFields.ts)
// because a school configures it; these two are fixed.

export type PublicFieldType = 'text' | 'email' | 'tel' | 'textarea';

export interface PublicFormField {
  key: string;
  label: string;
  type: PublicFieldType;
  required: boolean;
  maxLength: number;
  /** Shown under the field, and in the error when the pattern fails. */
  help?: string;
}

export interface PublicFormSpec {
  key: PublicFormKey;
  title: string;
  fields: PublicFormField[];
}

export type PublicFormKey = 'contact' | 'complaint';

export const PUBLIC_FORMS: Record<PublicFormKey, PublicFormSpec> = {
  contact: {
    key: 'contact',
    title: 'Contact us',
    fields: [
      { key: 'name', label: 'Your name', type: 'text', required: true, maxLength: 200 },
      { key: 'email', label: 'Email', type: 'email', required: true, maxLength: 200 },
      { key: 'subject', label: 'Subject', type: 'text', required: false, maxLength: 300 },
      { key: 'message', label: 'Message', type: 'textarea', required: true, maxLength: 5000 },
    ],
  },
  complaint: {
    key: 'complaint',
    title: 'Register a complaint',
    fields: [
      { key: 'name', label: 'Your name', type: 'text', required: true, maxLength: 200 },
      { key: 'email', label: 'Email', type: 'email', required: true, maxLength: 200 },
      { key: 'contact_no', label: 'Mobile number', type: 'tel', required: false, maxLength: 20, help: '10 digits' },
      { key: 'description', label: 'What happened', type: 'textarea', required: true, maxLength: 5000 },
    ],
  },
};

// Deliberately permissive: it rejects what is certainly not an address rather
// than trying to encode RFC 5322, which real addresses lose to.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^[0-9]{10}$/;

export interface PublicFormErrors {
  [field: string]: string;
}

/**
 * Check one form's values. Returns a field -> message map; empty means valid.
 * The same function runs in the browser and on the server, so what the page
 * accepts and what the route accepts cannot drift apart.
 */
export function validatePublicForm(
  formKey: PublicFormKey,
  values: Record<string, unknown>,
): PublicFormErrors {
  const spec = PUBLIC_FORMS[formKey];
  const errors: PublicFormErrors = {};
  if (!spec) return { form: 'Unknown form' };

  for (const f of spec.fields) {
    const raw = values[f.key];
    const value = typeof raw === 'string' ? raw.trim() : raw == null ? '' : String(raw).trim();

    if (!value) {
      if (f.required) errors[f.key] = `${f.label} is required`;
      continue;
    }
    if (value.length > f.maxLength) {
      errors[f.key] = `${f.label} may be at most ${f.maxLength} characters`;
      continue;
    }
    if (f.type === 'email' && !EMAIL_RE.test(value)) {
      errors[f.key] = 'Enter a valid email address';
      continue;
    }
    if (f.type === 'tel' && !PHONE_RE.test(value.replace(/[\s-]/g, ''))) {
      errors[f.key] = 'Enter a 10-digit mobile number';
    }
  }
  return errors;
}

/** The first error, for a form that shows one message rather than per-field. */
export function firstPublicFormError(errors: PublicFormErrors): string | null {
  const keys = Object.keys(errors);
  return keys.length ? errors[keys[0]] : null;
}

/** Values trimmed and cut to their maximum, ready to store. */
export function normalisePublicForm(
  formKey: PublicFormKey,
  values: Record<string, unknown>,
): Record<string, string> {
  const spec = PUBLIC_FORMS[formKey];
  const out: Record<string, string> = {};
  if (!spec) return out;
  for (const f of spec.fields) {
    const raw = values[f.key];
    out[f.key] = (typeof raw === 'string' ? raw : raw == null ? '' : String(raw)).trim().slice(0, f.maxLength);
  }
  return out;
}
